import { LogIn, LogOut, LayoutDashboard } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { useAuth } from '../../hooks/useAuth';
import { useLocation } from 'react-router-dom';
import { useState } from 'react';

interface HeaderProps {
  onSignIn: () => void;
}

export function Header({ onSignIn }: HeaderProps) {
  const { user, signOut } = useAuth();
  const { user: clerkUser } = useUser();
  const location = useLocation();
  const [logoError, setLogoError] = useState(false);

  const currentPath = location.pathname;
  const isLanding = !user && currentPath === '/';

  // On landing page (unauthenticated), use dark transparent nav that sits over the dark hero
  const navClass = isLanding
    ? 'fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-gray-950/80 backdrop-blur-md'
    : 'sticky top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md shadow-sm';

  const logoTextClass = isLanding ? 'text-white' : 'text-indigo-700';
  const subTextClass = isLanding ? 'text-gray-400' : 'text-gray-400';
  const linkBase = isLanding
    ? 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors text-gray-300 hover:text-white hover:bg-white/10'
    : 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors text-gray-600 hover:text-indigo-600 hover:bg-indigo-50';

  return (
    <header className={navClass}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-2">
            {!logoError ? (
              <img
                src="/fam-logo.png"
                alt="FAM"
                className="h-10 w-10 object-contain"
                style={{ mixBlendMode: isLanding ? 'normal' : 'multiply' }}
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed,#e11d48)' }}>
                <span className="text-white font-black text-sm">F</span>
              </div>
            )}
            <span className={`font-extrabold text-xl ${logoTextClass}`}>FAM</span>
            <span className={`font-normal text-sm hidden lg:inline ${subTextClass}`}>
              Family &amp; More
            </span>
          </a>

          <div className="flex items-center gap-1 md:gap-2">
            {user && (
              <>
                <a href="/"
                  className={`${linkBase} ${currentPath === '/' ? (isLanding ? 'bg-white/15 text-white' : 'bg-indigo-100 text-indigo-700') : ''}`}>
                  <LayoutDashboard className="w-4 h-4" />
                  <span className="hidden md:inline">Dashboard</span>
                </a>
                <div className={`w-px h-5 mx-1 hidden lg:block ${isLanding ? 'bg-white/15' : 'bg-gray-200'}`} />
                <span className={`hidden lg:inline text-sm font-medium ${isLanding ? 'text-gray-300' : 'text-gray-700'}`}>
                  {clerkUser?.firstName ?? user?.name?.split(' ')[0] ?? 'Account'}
                </span>
              </>
            )}

            <a href="/campaigns"
              className={`${linkBase} ${currentPath === '/campaigns' ? (isLanding ? 'bg-white/15 text-white' : 'bg-rose-100 text-rose-700') : ''}`}>
              Campaigns
            </a>

            <a href="/news"
              className={`hidden lg:flex ${linkBase} ${currentPath.startsWith('/news') ? (isLanding ? 'bg-white/15 text-white' : 'bg-indigo-100 text-indigo-700') : ''}`}>
              News
            </a>

            {/* Get Funded CTA — links to funding app */}
            <a
              href="https://funding.fam.autos"
              className="inline-flex items-center gap-1.5 px-3 md:px-4 py-1.5 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90"
              style={{ background: 'linear-gradient(135deg,#f43f5e,#f97316)', boxShadow: '0 2px 12px rgba(244,63,94,0.3)' }}
            >
              <span className="hidden sm:inline">Get Funded</span>
              <span className="sm:hidden">Fund</span>
            </a>

            {!user && currentPath !== '/dealers' && currentPath !== '/dealer' && (
              <a href="https://dealers.fam.autos"
                className={`hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${isLanding ? 'text-gray-300 border-white/15 hover:bg-white/10 hover:text-white' : 'text-indigo-600 border-indigo-200 hover:bg-indigo-50'}`}>
                For Dealers
              </a>
            )}

            {user ? (
              <button
                onClick={() => signOut()}
                className={`flex items-center gap-2 transition-colors px-2 py-1.5 ${isLanding ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-indigo-600'}`}>
                <LogOut className="w-5 h-5" />
                <span className="hidden lg:inline">Sign Out</span>
              </button>
            ) : (
              <button
                onClick={onSignIn}
                className={`flex items-center gap-2 transition-colors px-2 py-1.5 ${isLanding ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-indigo-600'}`}>
                <LogIn className="w-5 h-5" />
                <span className="hidden md:inline">Sign In</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
